import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import type { ForumLang } from "../constants/forumTranslations";

const LANG_STORAGE_KEY = "forumLang";
const BRAND_NAVY_LIGHT = "#2a2f55";

export const TermsOfUse: React.FC = () => {
  const [lang, setLang] = useState<ForumLang>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === "es" || stored === "en") return stored;
    } catch {
      // ignore
    }
    return "es";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const isEs = lang === "es";

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col overflow-x-hidden">
      {/* Header: same format as Forum (no stepper) */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <Link to="/" className="flex items-center space-x-5">
              <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo/logo.png"
                  alt="American School"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="border-l border-gray-100 pl-5 py-1">
                <h1
                  className="text-xl font-black tracking-tight uppercase leading-none"
                  style={{ color: "#161b3d" }}
                >
                  American School
                </h1>
                <p
                  className="text-xs font-bold mt-1 tracking-wider uppercase"
                  style={{ color: "#8b1e22" }}
                >
                  {isEs ? "Términos de uso" : "Terms of Use"}
                </p>
              </div>
            </Link>
          </div>

          <div className="flex rounded border border-gray-200/80 bg-gray-50/60 p-px" role="group" aria-label={isEs ? "Idioma" : "Language"}>
            <button
              type="button"
              onClick={() => setLang("es")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "es"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "es" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "en"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "en" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <motion.main
        className="flex-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-16">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900 text-center mb-3 uppercase">
            {isEs ? "Términos de uso" : "Terms of Use"}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 text-center max-w-2xl mx-auto uppercase tracking-wide mb-2">
            {isEs
              ? "Los presentes Términos de Uso regulan el acceso y utilización de la plataforma de inscripción y pagos en línea de American School."
              : "These Terms of Use govern access to and use of American School's online enrollment and payment platform."}
          </p>
          <p className="text-xs md:text-sm text-gray-500 text-center max-w-2xl mx-auto uppercase tracking-wide mb-10">
            {isEs
              ? "Al utilizar esta plataforma, el usuario acepta cumplir con las condiciones establecidas a continuación."
              : "By using this platform, the user agrees to comply with the conditions set forth below."}
          </p>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 text-center">
                {isEs ? "Términos de uso" : "Terms of Use"}
              </h2>
            </div>

            <ol className="divide-y divide-gray-100">
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">1. {isEs ? "Uso de la plataforma" : "Use of the Platform"}</p>
                <p>
                  {isEs
                    ? "Esta plataforma permite a los usuarios realizar procesos de inscripción y pago para clases, actividades o programas ofrecidos por American School."
                    : "This platform allows users to complete enrollment and payment processes for classes, activities or programs offered by American School."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">2. {isEs ? "Información proporcionada" : "Information Provided"}</p>
                <p>
                  {isEs
                    ? "El usuario se compromete a proporcionar información veraz, completa y actualizada durante el proceso de inscripción, incluyendo los datos del estudiante y del responsable del pago."
                    : "The user agrees to provide truthful, complete and up-to-date information during the enrollment process, including student and payer details."}
                </p>
                <p className="mt-2">
                  {isEs
                    ? "American School no se hace responsable por inconvenientes derivados de información incorrecta o incompleta proporcionada por el usuario."
                    : "American School is not responsible for any issues arising from incorrect or incomplete information provided by the user."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">3. {isEs ? "Confirmación de inscripción" : "Enrollment Confirmation"}</p>
                <p>
                  {isEs
                    ? "La inscripción a una clase o actividad se considera confirmada únicamente cuando el pago correspondiente ha sido procesado correctamente y registrado en la plataforma."
                    : "Enrollment in a class or activity is considered confirmed only when the corresponding payment has been successfully processed and recorded on the platform."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">4. {isEs ? "Modificaciones de servicios" : "Modifications of Services"}</p>
                <p>
                  {isEs
                    ? "American School se reserva el derecho de modificar fechas, horarios o disponibilidad de clases por razones operativas o situaciones imprevistas. En dichos casos, se informará oportunamente a los usuarios registrados."
                    : "American School reserves the right to modify dates, schedules or class availability for operational reasons or unforeseen circumstances. In such cases, registered users will be informed in a timely manner."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">5. {isEs ? "Uso adecuado" : "Proper Use"}</p>
                <p>
                  {isEs
                    ? "El usuario se compromete a utilizar la plataforma de manera responsable y conforme a las leyes y regulaciones vigentes de la República Dominicana."
                    : "The user agrees to use the platform responsibly and in accordance with the laws and regulations in force in the Dominican Republic."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">6. {isEs ? "Aceptación de los términos" : "Acceptance of the Terms"}</p>
                <p>
                  {isEs
                    ? "Al realizar una inscripción o pago mediante esta plataforma, el usuario confirma haber leído y aceptado los presentes Términos de Uso."
                    : "By completing an enrollment or payment through this platform, the user confirms having read and accepted these Terms of Use."}
                </p>
              </li>
            </ol>
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default TermsOfUse;

